/**
 * The "Did we change anything?" look-back (ENG-2244, after SUP-793: a login
 * failed for 12 days while the card only looked back 24 h). Pure helpers so
 * the query, the copy and the marker placement are tested without a render.
 */
import { ChangesWindow, SiteChanges } from "../troubleshootingInterfaces";
import { formatRelative } from "./formatters";

/** admin keeps 30 days of change history (`hours` 1..720). */
export const MAX_CHANGES_HOURS = 720;
const HOUR_MS = 3_600_000;

export type ChangesWindowChoice = "24h" | "7d" | "30d" | "sinceLastAlert";

export const DEFAULT_CHANGES_WINDOW: ChangesWindow = {
  kind: "hours",
  hours: 24,
};

export const WINDOW_CHOICES: { id: ChangesWindowChoice; label: string }[] = [
  { id: "24h", label: "24 h" },
  { id: "7d", label: "7 d" },
  { id: "30d", label: "30 d" },
  { id: "sinceLastAlert", label: "Since last alert" },
];

const CHOICE_HOURS: Record<
  Exclude<ChangesWindowChoice, "sinceLastAlert">,
  number
> = { "24h": 24, "7d": 168, "30d": MAX_CHANGES_HOURS };

/** Is `iso` within admin's 30-day reach of `nowMs`? */
export const withinMaxWindow = (
  iso: string | null | undefined,
  nowMs = Date.now()
): boolean => {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) && nowMs - t <= MAX_CHANGES_HOURS * HOUR_MS;
};

/**
 * The window for a picker choice. "Since last alert" needs the last alert
 * time from the current response; null when there is none. An alert older
 * than 30 days is clamped to the 30-day window.
 */
export const windowForChoice = (
  choice: ChangesWindowChoice,
  lastAlertAt: string | null | undefined,
  nowMs = Date.now()
): ChangesWindow | null => {
  if (choice !== "sinceLastAlert")
    return { kind: "hours", hours: CHOICE_HOURS[choice] };
  if (!lastAlertAt || !Number.isFinite(new Date(lastAlertAt).getTime()))
    return null;
  return {
    kind: "sinceLastAlert",
    since: lastAlertAt,
    clamped: !withinMaxWindow(lastAlertAt, nowMs),
  };
};

export const choiceForWindow = (window: ChangesWindow): ChangesWindowChoice => {
  if (window.kind === "sinceLastAlert") return "sinceLastAlert";
  if (window.hours >= MAX_CHANGES_HOURS) return "30d";
  if (window.hours >= 168) return "7d";
  return "24h";
};

/** The query string for `customer/{id}/changes/`. */
export const changesQuery = (window: ChangesWindow): string => {
  if (window.kind === "hours")
    return `hours=${Math.min(
      Math.max(Math.round(window.hours), 1),
      MAX_CHANGES_HOURS
    )}`;
  // A clamped "since last alert" asks for the whole 30 days rather than a
  // `since` admin would refuse or silently cut.
  if (window.clamped) return `hours=${MAX_CHANGES_HOURS}`;
  return `since=${encodeURIComponent(window.since)}`;
};

/** Window length in hours, for the report and older copy. */
export const windowHoursOf = (window: ChangesWindow, nowMs = Date.now()) => {
  if (window.kind === "hours") return window.hours;
  if (window.clamped) return MAX_CHANGES_HOURS;
  const t = new Date(window.since).getTime();
  return Math.max(1, Math.ceil((nowMs - t) / HOUR_MS));
};

const hoursPhrase = (hours: number) =>
  hours >= 48 && hours % 24 === 0 ? `${hours / 24} d` : `${hours} h`;

const windowOf = (changes: SiteChanges): ChangesWindow =>
  changes.window ?? { kind: "hours", hours: changes.windowHours };

/**
 * The window in a sentence: "the last 24 h", "the last 30 d", "since the last
 * alert (12 d ago)". A clamped since-last-alert is the last 30 d.
 */
export const windowPhrase = (changes: SiteChanges, nowMs = Date.now()) => {
  const window = windowOf(changes);
  if (window.kind === "sinceLastAlert" && !window.clamped)
    return `since the last alert (${formatRelative(window.since, nowMs)})`;
  return `the last ${hoursPhrase(windowHoursOf(window, nowMs))}`;
};

/** The window for a pill: "in 24 h", "in 7 d", "since last alert". */
export const windowShort = (changes: SiteChanges, nowMs = Date.now()) => {
  const window = windowOf(changes);
  if (window.kind === "sinceLastAlert" && !window.clamped)
    return "since last alert";
  return `in ${hoursPhrase(windowHoursOf(window, nowMs))}`;
};

/** The clamp, said out loud; null when the window was not clamped. */
export const clampNotice = (
  changes: SiteChanges,
  nowMs = Date.now()
): string | null => {
  const window = windowOf(changes);
  if (window.kind !== "sinceLastAlert" || !window.clamped) return null;
  return `The last alert was ${formatRelative(
    window.since,
    nowMs
  )}, before the 30 days of history we keep, so this shows the last 30 d.`;
};

export type MarkerKind = "lastAlert" | "lastConnected";

export interface WindowMarker {
  kind: MarkerKind;
  at: string;
  label: string;
}

/** "Last alert", "Last video received" or "Last successful connection (lower bound)". */
export const markerLabel = (
  kind: MarkerKind,
  source: string | null | undefined
): string => {
  if (kind === "lastAlert") return "Last alert";
  return source === "last_frame"
    ? "Last video received"
    : "Last successful connection (lower bound)";
};

/**
 * The newest of several alert times, or null. Admin's `lastAlertAt` marker
 * can lag the alerts this page already loaded (QA, ENG-2187), so "Last alert"
 * and "Since last alert" take the newest of them all.
 */
export const newestAlertAt = (
  ...candidates: (string | null | undefined)[]
): string | null => {
  let newest: string | null = null;
  let newestMs = -Infinity;
  for (const at of candidates) {
    const t = at ? new Date(at).getTime() : NaN;
    if (at && Number.isFinite(t) && t > newestMs) {
      newest = at;
      newestMs = t;
    }
  }
  return newest;
};

/** `changes` with its "Last alert" marker moved up to `lastAlertAt`. */
export const withLastAlert = (
  changes: SiteChanges,
  lastAlertAt: string | null
): SiteChanges => {
  if (!lastAlertAt || lastAlertAt === changes.markers?.lastAlertAt)
    return changes;
  return {
    ...changes,
    markers: {
      lastConnectedAt: null,
      lastConnectedSource: null,
      ...changes.markers,
      lastAlertAt,
    },
  };
};

/**
 * Markers split by the window: those inside it go on the timeline, older ones
 * become header lines. Null markers are dropped.
 */
export const placeMarkers = (
  changes: SiteChanges
): { inWindow: WindowMarker[]; beforeWindow: WindowMarker[] } => {
  const markers = changes.markers;
  const out = {
    inWindow: [] as WindowMarker[],
    beforeWindow: [] as WindowMarker[],
  };
  if (!markers) return out;
  const start = new Date(changes.since).getTime();
  const add = (kind: MarkerKind, at: string | null) => {
    if (!at) return;
    const t = new Date(at).getTime();
    if (!Number.isFinite(t)) return;
    const marker = {
      kind,
      at,
      label: markerLabel(kind, markers.lastConnectedSource),
    };
    if (Number.isFinite(start) && t < start) out.beforeWindow.push(marker);
    else out.inWindow.push(marker);
  };
  add("lastAlert", markers.lastAlertAt);
  add("lastConnected", markers.lastConnectedAt);
  return out;
};

/**
 * The header line for a marker older than the window, with whether widening
 * to 30 d would bring it in.
 */
export const beforeWindowLine = (
  marker: WindowMarker,
  changes: SiteChanges,
  nowMs = Date.now()
): { text: string; canWiden: boolean } => {
  const window = windowOf(changes);
  const atMax = windowHoursOf(window, nowMs) >= MAX_CHANGES_HOURS;
  const reachable = withinMaxWindow(marker.at, nowMs);
  const when = formatRelative(marker.at, nowMs);
  if (reachable && !atMax)
    return {
      text: `${marker.label}: ${when}, before this window.`,
      canWiden: true,
    };
  return {
    text: `${marker.label}: ${when}, before the 30 days of history we keep.`,
    canWiden: false,
  };
};
